import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AuthPrompt from './AuthPrompt';
import { PLAYBOOK_DESCRIPTIONS } from '../data/mockPlaybooks';
import { PLAYBOOK_TYPES } from '../types/picks';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import Pick from './Pick';
import SortButton from './SortButton';
import PlaybookCard from './PlaybookCard';

// Format number with commas and decimal places
const formatCurrency = (number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

// Format date string to readable format
const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  
  // Handle Firestore Timestamp objects
  if (dateString && typeof dateString === 'object' && dateString.toDate) {
    const date = dateString.toDate();
    if (includeTime) {
      return `${date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      })} @ ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase()}`;
    }
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  }
  
  // Handle regular date strings
  const date = new Date(dateString);
  if (includeTime) {
    return `${date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    })} @ ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase()}`;
  }
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};

const PlaybookSelector = ({ playbook, description, onChange }) => (
  <div className="content-section">
    <div className="section-header">
      <h2>Select a Playbook</h2>
    </div>
    <select 
      value={playbook}
      onChange={onChange}
      className="selector-input"
    >
      {Object.values(PLAYBOOK_TYPES).map(type => (
        <option key={type} value={type}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </option>
      ))}
    </select>
    <div className="selector-description">
      <p>{description}</p>
    </div>
  </div>
);

const EventSelector = ({ event, events, onChange, lastUpdated }) => (
  <div className="content-section">
    <div className="section-header">
      <h2>Pick an Event</h2>
    </div>
    <select 
      value={event?.id || ''}
      onChange={onChange}
      className="selector-input"
    >
      {events.map(evt => (
        <option key={evt.id} value={evt.id}>{evt.name}</option>
      ))}
    </select>
    {event && (
      <div className="selector-info">
        <div className="info-row">
          <span className="info-label">Event Date:</span>
          <span className="info-value">{formatDate(event.date)}</span>
        </div>
        {lastUpdated && (
          <div className="info-row">
            <span className="info-label">Last Updated:</span>
            <span className="info-value">{formatDate(lastUpdated, true)}</span>
          </div>
        )}
      </div>
    )}
  </div>
);

const BudgetInput = ({ value, onChange }) => {
  const handleChange = (e) => {
    const cleanValue = e.target.value.replace(/[^\d.]/g, '');
    const decimalCount = (cleanValue.match(/\./g) || []).length;
    if (decimalCount <= 1) {
      onChange(cleanValue);
    }
  };

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>Enter Your Budget</h2>
      </div>
      <div className="budget-input">
        <span className="currency-symbol">$</span>
        <input
          type="text"
          value={value}
          onChange={handleChange}
          className="selector-input"
          placeholder="Enter your budget"
        />
      </div>
    </div>
  );
};

const TotalWager = ({ picks, budget }) => {
  const effectiveBudget = parseFloat(budget) || 0;
  if (!picks || picks.length === 0) return null;
  
  const totalWager = picks.reduce((total, pick) => {
    const amount = pick.budget_amount || pick.allocation || 0;
    return total + (effectiveBudget * amount);
  }, 0).toFixed(2);

  const totalAllocation = picks.reduce((total, pick) => total + (pick.budget_amount || pick.allocation || 0), 0) * 100;
  
  const totalPotentialWin = picks.reduce((total, pick) => {
    const betAmount = effectiveBudget * (pick.budget_amount || pick.allocation || 0);
    const win = betAmount * (pick.decimal_odds || pick.decimal || 1);
    return total + win;
  }, 0).toFixed(2);
  
  const returnMultiplier = (parseFloat(totalPotentialWin) / parseFloat(totalWager)).toFixed(2);
  
  return (
    <div className="total-wager">
      <div className="wager-amount">
        Total Wager: <strong>${formatCurrency(parseFloat(totalWager))}</strong> 
        <span className="allocation-note"> ({totalAllocation.toFixed(1)}% of budget)</span>
      </div>
      <div className="potential-win">
        Potential Win: <strong>${formatCurrency(parseFloat(totalPotentialWin))}</strong> 
        <span className="allocation-note"> ({returnMultiplier}x)</span>
      </div>
      <div className="bet-count">
        Recommended Bets: <strong>{picks.length}</strong>
      </div>
    </div>
  );
};

export default function Picks() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('odikai');
  const [selectedPlaybook, setSelectedPlaybook] = useState(PLAYBOOK_TYPES.BASELINE);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [budget, setBudget] = useState('500');
  const [currentPlaybook, setCurrentPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPlaybooks, setUserPlaybooks] = useState([]);
  const [sortField, setSortField] = useState('budget_amount');
  const [sortDirection, setSortDirection] = useState('desc');

  // Fetch events from Firebase
  useEffect(() => {
    async function fetchEvents() {
      try {
        const eventsRef = collection(db, 'events');
        const eventsSnapshot = await getDocs(eventsRef);
        const eventsData = [];
        eventsSnapshot.forEach((doc) => {
          eventsData.push(doc.data());
        });
        
        // Filter events to only show those within 1 day of today or in the future
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const filteredEvents = eventsData.filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= oneDayAgo;
        });
        
        // Sort filtered events by date
        filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
        setEvents(filteredEvents);
        
        // Set the first event as selected if none is selected
        if (!selectedEvent && filteredEvents.length > 0) {
          setSelectedEvent(filteredEvents[0]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      }
    }
    fetchEvents();
  }, [selectedEvent]);

  // Calculate estimated win for a pick based on budget and decimal odds
  const calculateEstimatedWin = (budgetAmount, decimalOdds) => {
    const effectiveBudget = parseFloat(budget) || 0;
    if (!budgetAmount || !decimalOdds) return '0.00';
    const betAmount = effectiveBudget * (budgetAmount || 0);
    return (betAmount * (decimalOdds || 1)).toFixed(2);
  };

  // Fetch the playbook based on strategy type and event
  useEffect(() => {
    async function fetchPlaybook() {
      // Only fetch if we're on the Odikai tab
      if (!selectedEvent || activeTab !== 'odikai') return;

      setLoading(true);
      try {
        // Get the specific playbook document using the combined ID
        const playbook_id = `${selectedPlaybook}_${selectedEvent.id}`;
        const playbookRef = doc(db, 'prebuilt_playbooks', playbook_id);
        const playbookDoc = await getDoc(playbookRef);
        
        if (playbookDoc.exists()) {
          setCurrentPlaybook(playbookDoc.data());
        } else {
          setCurrentPlaybook(null);
        }
      } catch (error) {
        console.error('Error fetching playbook:', error);
        setCurrentPlaybook(null);
      }
      setLoading(false);
    }

    fetchPlaybook();
  }, [selectedPlaybook, selectedEvent, activeTab]);

  // Get picks from currentPlaybook, ensuring it exists and has picks
  const picks = currentPlaybook?.picks || [];

  // Function to handle selecting a user playbook
  const handleSelectUserPlaybook = (playbook) => {
    setCurrentPlaybook(playbook);
  };

  // Sort picks based on current sort field and direction
  const sortPicks = (picksToSort) => {
    if (!picksToSort) return [];
    const effectiveBudget = parseFloat(budget) || 0;
    return [...picksToSort].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'budget_amount':
          aValue = effectiveBudget * (a.budget_amount || a.allocation || 0);
          bValue = effectiveBudget * (b.budget_amount || b.allocation || 0);
          break;
        case 'odds':
          aValue = a.decimal_odds || a.decimal || 0;
          bValue = b.decimal_odds || b.decimal || 0;
          break;
        case 'win':
          aValue = calculateEstimatedWin(a.budget_amount || a.allocation || 0, a.decimal_odds || a.decimal || 0);
          bValue = calculateEstimatedWin(b.budget_amount || b.allocation || 0, b.decimal_odds || b.decimal || 0);
          break;
        case 'supporting_strategies':
          aValue = a.supporting_strategies || 0;
          bValue = b.supporting_strategies || 0;
          break;
        default:
          return 0;
      }
      
      return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
    });
  };

  // Handle sort button click
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  useEffect(() => {
    if (selectedEvent) {
      // ... existing code ...
    }
  }, [selectedEvent, activeTab, user]);

  return (
    <>
      <style jsx>{`
        .page-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
        }

        .tabs-container {
          display: flex;
          gap: 1px;
          background: var(--surface-light);
          padding: 4px;
          border-radius: 8px;
          margin-bottom: 24px;
          width: fit-content;
        }

        .tab {
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tab:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .tab.active {
          background: var(--primary);
          color: white;
        }

        .content-grid {
          display: grid;
          gap: 24px;
        }

        .content-section {
          background: var(--surface-light);
          border-radius: 12px;
          padding: 24px;
        }

        .section-header {
          margin-bottom: 24px;
        }

        .section-header h2 {
          font-size: 24px;
          font-weight: 600;
          margin: 0;
          color: white;
        }

        .selector-input {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          color: white;
          padding: 12px;
          font-size: 14px;
          width: 100%;
          transition: all 0.2s ease;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>');
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          padding-right: 40px;
          margin-bottom: 16px;
        }

        .selector-input option {
          background: #1E1E1E;
          color: white;
          padding: 8px;
        }

        .selector-input:hover {
          border-color: rgba(255, 255, 255, 0.2);
        }

        .selector-input:focus {
          border-color: var(--primary);
          outline: none;
        }

        .selector-description,
        .selector-info {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 6px;
          padding: 16px;
        }

        .selector-description p {
          font-size: 14px;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
        }

        .info-row + .info-row {
          margin-top: 8px;
        }

        .info-label {
          color: rgba(255, 255, 255, 0.6);
        }

        .info-value {
          color: white;
        }

        .budget-input {
          display: flex;
          align-items: center;
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          transition: all 0.2s ease;
          width: 100%;
          box-sizing: border-box;
          height: 42px; /* Match height of other inputs */
        }

        .budget-input:hover {
          border-color: rgba(255, 255, 255, 0.2);
        }

        .budget-input:focus-within {
          border-color: var(--primary);
        }

        .currency-symbol {
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
          padding-left: 12px;
        }

        .budget-input .selector-input {
          margin: 0;
          border: none;
          background: transparent;
          padding: 0 12px;
          width: 100%;
          height: 100%;
          box-sizing: border-box;
        }

        .picks-section {
          background: var(--surface-light);
          border-radius: 12px;
          padding: 24px;
        }

        .total-wager {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .sort-controls {
          display: flex;
          gap: 8px;
          margin: 20px 0;
        }

        .picks-grid {
          display: grid;
          gap: 16px;
        }

        .playbook-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .playbook-card {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .playbook-card:hover {
          border-color: var(--primary);
          background: rgba(123, 73, 209, 0.05);
        }

        .playbook-card.active {
          border-color: var(--primary);
          background: rgba(123, 73, 209, 0.1);
        }

        .playbook-card h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 20px 0;
          color: white;
        }

        .playbook-stats {
          display: grid;
          gap: 12px;
        }

        .stat-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
          font-size: 14px;
        }

        .stat-label {
          color: rgba(255, 255, 255, 0.6);
        }

        .stat-value {
          color: white;
          font-weight: 500;
        }

        .stat-value.highlight {
          color: white;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: rgba(255, 255, 255, 0.6);
        }

        .action-button {
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 6px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 16px;
        }

        .action-button:hover {
          background: #8b59e1;
        }

        @media (max-width: 768px) {
          .page-container {
            padding: 16px;
          }

          .content-section {
            padding: 16px;
          }

          .section-header {
            margin-bottom: 16px;
          }

          .section-header h2 {
            font-size: 20px;
          }

          .tab {
            padding: 10px 16px;
            font-size: 13px;
          }

          .selector-input,
          .selector-description,
          .selector-info {
            padding: 12px;
          }

          .playbook-grid {
            grid-template-columns: 1fr;
          }

          .playbook-card h3 {
            font-size: 16px;
            margin-bottom: 16px;
          }

          .stat-row {
            padding: 6px;
            font-size: 13px;
          }

          .sort-controls {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
            margin: 16px 0;
          }
        }

        .page-header {
          background: linear-gradient(to right, rgba(123, 73, 209, 0.1), rgba(0, 0, 0, 0));
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border: 1px solid rgba(123, 73, 209, 0.2);
        }

        .header-content {
          flex: 1;
        }

        .header-content h1 {
          font-size: 36px;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0.8));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .header-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.6);
          margin: 8px 0 0 0;
          line-height: 1.5;
        }

        .header-stats {
          display: flex;
          gap: 16px;
          margin-left: 32px;
        }

        .stat-card {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          padding: 16px 24px;
          text-align: center;
          min-width: 120px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .stat-card .stat-value {
          display: block;
          font-size: 24px;
          font-weight: 600;
          color: var(--primary);
          margin-bottom: 4px;
        }

        .stat-card .stat-label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            padding: 24px;
            text-align: center;
            gap: 24px;
          }

          .header-content h1 {
            font-size: 28px;
          }

          .header-subtitle {
            font-size: 14px;
          }

          .header-stats {
            margin-left: 0;
            width: 100%;
            justify-content: center;
          }

          .stat-card {
            min-width: 100px;
            padding: 12px 16px;
          }
        }
      `}</style>

      <div className="page-container">
        <header className="page-header">
          <div className="header-content">
            <h1>Picks</h1>
            <p className="header-subtitle">Choose your playbook, select an event, and get AI-powered picks tailored to your budget.</p>
          </div>
          <div className="header-stats">
            <div className="stat-card">
              <span className="stat-value">{events.length}</span>
              <span className="stat-label">Upcoming Events</span>
            </div>
            {activeTab === 'my' && user && (
              <div className="stat-card">
                <span className="stat-value">{userPlaybooks.length}</span>
                <span className="stat-label">My Playbooks</span>
              </div>
            )}
          </div>
        </header>

        <div className="tabs-container">
          <button 
            className={`tab ${activeTab === 'odikai' ? 'active' : ''}`}
            onClick={async () => {
              setActiveTab('odikai');
              setSelectedPlaybook(PLAYBOOK_TYPES.BASELINE);
              if (selectedEvent) {
                setLoading(true);
                try {
                  const playbook_id = `${PLAYBOOK_TYPES.BASELINE}_${selectedEvent.id}`;
                  const playbookRef = doc(db, 'prebuilt_playbooks', playbook_id);
                  const playbookDoc = await getDoc(playbookRef);
                  if (playbookDoc.exists()) {
                    setCurrentPlaybook(playbookDoc.data());
                  } else {
                    setCurrentPlaybook(null);
                  }
                } catch (error) {
                  console.error('Error fetching playbook:', error);
                  setCurrentPlaybook(null);
                }
                setLoading(false);
              }
            }}
          >
            Odikai Playbooks
          </button>
          <button 
            className={`tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={async () => {
              setLoading(true);
              setActiveTab('my');
              setSelectedPlaybook(null);
              
              try {
                if (userPlaybooks.length > 0) {
                  setCurrentPlaybook(userPlaybooks[0]);
                } else if (user) {
                  const userPlaybooksRef = collection(db, 'user_playbooks');
                  const q = query(
                    userPlaybooksRef,
                    where('user_id', '==', user.uid)
                  );
                  
                  const querySnapshot = await getDocs(q);
                  const playbooks = [];
                  querySnapshot.forEach((doc) => {
                    playbooks.push({ id: doc.id, ...doc.data() });
                  });
                  
                  setUserPlaybooks(playbooks);
                  if (playbooks.length > 0) {
                    setCurrentPlaybook(playbooks[0]);
                  }
                }
              } catch (error) {
                console.error('Error fetching user playbooks:', error);
                setUserPlaybooks([]);
                setCurrentPlaybook(null);
              } finally {
                setLoading(false);
              }
            }}
          >
            My Playbooks
          </button>
        </div>

        {activeTab === 'my' && !user ? (
          <div className="auth-prompt-wrapper">
            <AuthPrompt />
          </div>
        ) : (
          <div className="content-grid">
            {activeTab === 'odikai' ? (
              <>
                <div className="selector-card">
                  <PlaybookSelector
                    playbook={selectedPlaybook}
                    description={PLAYBOOK_DESCRIPTIONS[selectedPlaybook]}
                    onChange={(e) => setSelectedPlaybook(e.target.value)}
                  />
                </div>

                <div className="selector-card">
                  <EventSelector
                    event={selectedEvent}
                    events={events}
                    onChange={(e) => setSelectedEvent(events.find(event => event.id === e.target.value))}
                    lastUpdated={currentPlaybook?.last_updated}
                  />
                </div>

                <div className="selector-card">
                  <BudgetInput
                    value={budget}
                    onChange={setBudget}
                  />
                </div>

                {budget !== undefined && (
                  <section className="picks-section">
                    <div className="section-header">
                      <h2>Picks</h2>
                      <TotalWager picks={picks} budget={budget} />
                      <div className="sort-controls">
                        <SortButton 
                          field="budget_amount" 
                          label="Sort by Bet" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                        <SortButton 
                          field="odds" 
                          label="Sort by Odds" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                        <SortButton 
                          field="win" 
                          label="Sort by Win" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                        {currentPlaybook?.strategies && (
                          <SortButton 
                            field="supporting_strategies" 
                            label="Sort by Support" 
                            currentField={sortField}
                            direction={sortDirection}
                            onClick={handleSort}
                          />
                        )}
                      </div>
                    </div>

                    {loading ? (
                      <div className="empty-state">Loading picks...</div>
                    ) : picks.length > 0 ? (
                      <div className="picks-grid">
                        {sortPicks(picks).map((pick, index) => (
                          <Pick
                            key={index}
                            pick_name={pick.pick_name || pick.fighter}
                            opponent_name={pick.opponent_name || pick.opponent}
                            type={pick.type || 'Unknown'}
                            weight={pick.budget_amount || pick.allocation}
                            american_odds={pick.american_odds || pick.odds}
                            decimal_odds={pick.decimal_odds || pick.decimal}
                            supporting_strategies={pick.supporting_strategies || 0}
                            total_strategies={currentPlaybook?.strategies || 10}
                            budget={parseFloat(budget || 0)}
                            showBudget={true}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        No picks available for this playbook.
                      </div>
                    )}
                  </section>
                )}
              </>
            ) : (
              <>
                <div className="selector-card">
                  <div className="playbook-grid">
                    {userPlaybooks.map((playbook) => (
                      <PlaybookCard
                        key={playbook.id}
                        playbook={playbook}
                        isActive={currentPlaybook?.id === playbook.id}
                        onClick={() => handleSelectUserPlaybook(playbook)}
                        showActions={false}
                      />
                    ))}
                  </div>
                </div>

                <div className="selector-card">
                  <BudgetInput
                    value={budget}
                    onChange={setBudget}
                  />
                </div>

                {currentPlaybook && (
                  <section className="picks-section">
                    <div className="section-header">
                      <h2>Picks for {currentPlaybook.name || 'Untitled Playbook'}</h2>
                      {currentPlaybook.event && (
                        <div className="selector-info">
                          <div className="info-row">
                            <span className="info-label">Event:</span>
                            <span className="info-value">{currentPlaybook.event.name}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Date:</span>
                            <span className="info-value">{formatDate(currentPlaybook.event.date)}</span>
                          </div>
                        </div>
                      )}
                      <TotalWager picks={currentPlaybook.picks || []} budget={budget} />
                      <div className="sort-controls">
                        <SortButton 
                          field="budget_amount" 
                          label="Sort by Bet" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                        <SortButton 
                          field="odds" 
                          label="Sort by Odds" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                        <SortButton 
                          field="win" 
                          label="Sort by Win" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                        <SortButton 
                          field="supporting_strategies" 
                          label="Sort by Support" 
                          currentField={sortField}
                          direction={sortDirection}
                          onClick={handleSort}
                        />
                      </div>
                    </div>

                    <div className="picks-grid">
                      {sortPicks(currentPlaybook.picks || []).map((pick, index) => (
                        <Pick
                          key={index}
                          pick_name={pick.pick_name || pick.fighter || 'Unknown Fighter'}
                          opponent_name={pick.opponent_name || pick.opponent || 'Unknown Opponent'}
                          type={pick.type || 'Unknown'}
                          weight={pick.budget_amount || pick.allocation}
                          american_odds={pick.american_odds || pick.odds}
                          decimal_odds={pick.decimal_odds || pick.decimal}
                          supporting_strategies={pick.supporting_strategies || 0}
                          total_strategies={currentPlaybook.strategies || 10}
                          budget={parseFloat(budget || 0)}
                          showBudget={true}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {!loading && userPlaybooks.length === 0 && (
                  <div className="empty-state">
                    <p>You haven't created any playbooks yet.</p>
                    <button className="action-button">Create Your First Playbook</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
} 